/* eslint-disable max-len */
import React, { useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import useWellnessPlans from "../../../hooks/useWellnessPlans";
import { usePrequalificationInfo } from "../../../services/prequalification";

import { MIXPANEL_EVENTS } from "../../../config/mixPanelEvents";
import { PAGES_PATH } from "../../../navigation/params";
import PlanCategorySelectionCardList from "./PlanCategorySelectionCardList";
import { WellnessPlanErrorView } from "..";
import { useWellnessPlanErrorCta } from "../WellnessPlanErrorPage/useWellnessPlanErrorCta";
import type { WellnessPlanLocalErrorKind } from "../WellnessPlanErrorPage/wellnessPlanError.helpers";
import { SERVICE_STATUS_VALUES } from "../../../config/params";
import { Skeleton } from "@mui/material";
import { StyledBookingFlowButton } from "../BookingScheduleContainer/BookingScheduleContainer.style";
import WellnessPlanDetails from "./WellnessPlanDetails";
import WellnessPlanPetDetailsCard from "./WellnessPlanPetDetailsCard";
import { useAllServiceStatus } from "../../../services/all-service-status/all-service-status.service";
import { useAllServiceStatusStore } from "../../../hooks";
import { usePetStore } from "../../../hooks/redux-hooks/pet-store/pet-store.service";
import useWellnessPurchaseData from "../../../hooks/redux-hooks/wellness-purchase-store/wellness-purchase-store";
import { userActivityLogger } from "../../../utils/mixpanel";

const WellnessPlanSelectionContainer = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { petList } = usePetStore();
  const petId = searchParams.get("id");

  // An INELIGIBLE prequalification limits the customer to annual billing from
  // every entry point (SGPD-587), so the latest record is read here rather
  // than carried in from the prequal screens.
  const {
    fetchPrequalificationInfo,
    prequalificationInfo,
    error: prequalError,
  } = usePrequalificationInfo();
  const prequalPending = !prequalError && prequalificationInfo === undefined;
  const isAnnualOnly = prequalificationInfo?.status === "INELIGIBLE";

  useEffect(() => {
    fetchPrequalificationInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentPet = useMemo(
    () => petList?.filter((pet) => pet.id == petId),
    [petList, petId],
  );

  const resolveErrorCta = useWellnessPlanErrorCta({ petId });

  const { getAllServicesStatus, data } = useAllServiceStatus();
  const {
    setWellnessServiceStatus,
    isServiceStatusStoreUpdated,
    setServiceStatusStoreUpdated,
  } = useAllServiceStatusStore();

  const { wellnessPurchaseData, setWellnessPurchaseData } = useWellnessPurchaseData();

  const {
    annualSegregatedPlanData,
    annualAvailablePlans,
    fetchCatalogItems,
    loading,
    error,
    called,
    selectedPlan,
    setSelectedplan,
    getCurrentPlanData,
    planPricing,
  } = useWellnessPlans();

  const handlePlanSelectionNaviagtion = () => {
    if (currentPet) {
      const formattedBirthdate = currentPet[0].birthdate ?
        new Date(parseInt(currentPet[0].birthdate) * 1000).toLocaleDateString("en-US") :
        null;

      userActivityLogger(MIXPANEL_EVENTS.USER_WP_PLAN_SELECTION, {
        pet_id: currentPet[0].id,
        pet_name: currentPet[0].name,
        dob: formattedBirthdate,
        selected_plan: wellnessPurchaseData?.planType,
      });
    }
    navigate(
      PAGES_PATH.WELLNESS_PLAN_DETAILS +
        PAGES_PATH.WELLNESS_PLAN_CONFIRM_AND_PAY +
        "?id=" +
        petId,
    );
  };

  useEffect(() => {
    if (annualAvailablePlans.length) {
      const defaultPlan =
        annualAvailablePlans.find((plan) => plan.recommended)?.planValue ||
        annualAvailablePlans.find((plan) => plan.ageGroup === "adult")?.planValue ||
        annualAvailablePlans[0].planValue;

      setSelectedplan(defaultPlan);
    }
  }, [annualAvailablePlans]);

  const isWellnessServiceActive = data?.filter(
    (service) => service.serviceName === SERVICE_STATUS_VALUES.WELLNESS_PLAN_SERVICE,
  )[0]?.isActive;

  useEffect(() => {
    if (isWellnessServiceActive === false) {
      setWellnessServiceStatus(false);
    }
  }, [isWellnessServiceActive]);

  useEffect(() => {
    const isWellnessServiceActive = data?.filter(
      (service) => service.serviceName === SERVICE_STATUS_VALUES.WELLNESS_PLAN_SERVICE,
    )[0]?.isActive;

    if (petId && isWellnessServiceActive) {
      fetchCatalogItems(petId);
    }
  }, [petId, data]);

  useEffect(() => {
    if (prequalPending) return;
    const currentPlanData = getCurrentPlanData(selectedPlan);

    if (currentPlanData) {
      // Same fields usePrequalContinuation writes for a denied record on its
      // direct-to-payment path, so the payment page shows the denied state
      // without waiting for the ScratchPay eligibility round-trip.
      setWellnessPurchaseData({
        ...currentPlanData,
        ...(isAnnualOnly && {
          prequalStatus: "INELIGIBLE" as const,
          preferredBillingCycle: "perYear" as const,
        }),
      });
    }
  }, [selectedPlan, annualSegregatedPlanData, prequalPending, isAnnualOnly]);

  useEffect(() => {
    const wellnessRedirect = JSON.parse(localStorage.getItem("wellness-utm") || "{}");

    if (wellnessRedirect.isWellnessPageRedirect) {
      localStorage.setItem(
        "wellness-utm",
        JSON.stringify({ ...wellnessRedirect, isWellnessPageRedirect: false }),
      );
    }
    isServiceStatusStoreUpdated && setServiceStatusStoreUpdated(false);
  }, []);

  useEffect(() => {
    if (!isServiceStatusStoreUpdated) {
      getAllServicesStatus();
    }
  }, [isServiceStatusStoreUpdated]);

  const loadingCard = () => (
    <div className="w-full">
      <div className="w-full flex gap-3">
        <Skeleton animation="wave" variant="circular" width={80} height={80} />
        <div className="flex flex-col">
          <Skeleton animation="wave" width={"8rem"} height={30} />
          <Skeleton animation="wave" width={"12rem"} height={30} />
          <Skeleton animation="wave" width={"12rem"} height={30} />
        </div>
      </div>
      <div className="flex flex-col md:flex-row gap-4 mt-4 w-full">
        <div className="flex-1 flex flex-col gap-3">
          <div className="p-4 rounded-lg border border-taupe-50 bg-white">
            <Skeleton height={24} width={120} className="mb-2" />
            <Skeleton variant="text" width="60%" />
            <Skeleton variant="text" width="40%" />
          </div>
          <div className="p-4 rounded-lg border border-taupe-50 bg-white">
            <Skeleton height={24} width={120} className="mb-2" />
            <Skeleton variant="text" width="60%" />
            <Skeleton variant="text" width="40%" />
          </div>
        </div>
        <div className="flex-1 p-4 rounded-lg border border-taupe-50 bg-white">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="mb-4">
              <Skeleton height={24} width={100} className="mb-2" />
              <Skeleton variant="text" width="60%" />
              <Skeleton variant="text" width="40%" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const isPetListLoaded = petList && petList.length > 0;
  const isInvalidPet = !petId || (isPetListLoaded && (!currentPet || currentPet.length === 0));
  const isServiceUnavailable = isWellnessServiceActive === false;
  const isNoBirthdate = !!(currentPet && currentPet[0] && !currentPet[0].birthdate);
  const isEmptyPlans = called && !loading && !error && annualAvailablePlans.length === 0;

  const localErrorKind: WellnessPlanLocalErrorKind | null = (() => {
    if (isInvalidPet) return "PET_NOT_FOUND";
    if (isServiceUnavailable) return "SERVICE_UNAVAILABLE";
    if (isNoBirthdate) return "NO_BIRTHDATE";
    if (isEmptyPlans) return "NO_PLANS_AVAILABLE";
    return null;
  })();

  if (localErrorKind) {
    return (
      <WellnessPlanErrorView resolveCta={resolveErrorCta} localKind={localErrorKind} />
    );
  }

  if (error) {
    return <WellnessPlanErrorView resolveCta={resolveErrorCta} apolloError={error} />;
  }

  const isReady =
    !loading &&
    !prequalPending &&
    annualAvailablePlans.length > 0 &&
    currentPet &&
    wellnessPurchaseData;

  return (
    <div className="w-full flex justify-center">
      <div
        className="p-3 sm:p-6 border border-gray-3 bg-white rounded-xl flex flex-col items-stretch gap-5 -mx-4 sm:m-auto relative -top-12 md:-top-16 xl:-top-32
          sm:w-[90%] md:w-[85%] xl:w-[80%] 2xl:w-[960px]"
      >
        {isReady ? (
          <>
            <WellnessPlanPetDetailsCard />
            <div className="flex flex-col md:grid md:grid-cols-[2fr_3fr] md:items-stretch gap-4 w-full">
              <div className="w-full">
                <PlanCategorySelectionCardList
                  planDetails={annualAvailablePlans}
                  setSelectedplan={setSelectedplan}
                  currentPetDetails={currentPet[0]}
                  currentPlanType={wellnessPurchaseData?.planType}
                  selectedPlan={selectedPlan}
                  planPricing={planPricing}
                  annualOnly={isAnnualOnly}
                />
              </div>
              <div className="w-full">
                <WellnessPlanDetails
                  selectedPlan={selectedPlan}
                  planData={getCurrentPlanData(selectedPlan)}
                  petName={currentPet[0].name}
                />
              </div>
            </div>
            <div className="w-full">
              <StyledBookingFlowButton
                fullWidth
                color="primary"
                size="small"
                onClick={handlePlanSelectionNaviagtion}
                disabled={error}
              >
                Confirm Selected Plan
              </StyledBookingFlowButton>
            </div>
          </>
        ) : (
          loadingCard()
        )}
      </div>
    </div>
  );
};

export default WellnessPlanSelectionContainer;
